import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { validationContext, func } from 'validx'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { type Member } from '@/features/iam/members/Member'
import type {
  CreateCustomerFn,
  CreateMemberManuallyFn,
  FetchMemberFn,
} from '@/features/iam'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { tap } from 'rxjs'

export class CallCenterAddClientDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  @observable
  accessor email = ''

  @observable
  accessor firstName = ''

  @observable
  accessor lastName = ''

  @observable
  accessor phone = ''

  @observable
  accessor selectedWorkspace: Workspace | null = null

  readonly validation = validationContext(this, {
    email: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'Email is required.',
      ),
    ],
    firstName: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'First name is required.',
      ),
    ],
    lastName: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'Last name is required.',
      ),
    ],
    phone: [
      func(
        ({ value }) => typeof value === 'string' && value.trim().length > 0,
        'Phone number is required.',
      ),
    ],
    selectedWorkspace: [
      func(() => this.selectedWorkspace !== null, 'Workspace is required.'),
    ],
  })

  constructor(
    private readonly _createCustomer: CreateCustomerFn,
    private readonly _fetchMember: FetchMemberFn,
    private readonly _createMemberManually: CreateMemberManuallyFn,
    private readonly _getAvailableMemberships: () => Membership[],
    private readonly _onClientCreated: (membership: Membership) => void,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    this._clearValidationOnChange('email')
    this._clearValidationOnChange('firstName')
    this._clearValidationOnChange('lastName')
    this._clearValidationOnChange('phone')
    this._clearValidationOnChange('selectedWorkspace')
  }

  @computed
  get availableWorkspaces() {
    return this._getAvailableMemberships().map(
      (membership) => membership.workspace,
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  @action.bound
  show() {
    this.validation.reset()
    this.modal.show()

    if (!this.selectedWorkspace && this.availableWorkspaces.length === 1) {
      this.setWorkspace(this.availableWorkspaces[0] ?? null)
    }
  }

  @action.bound
  close() {
    this.reset()
    this.validation.reset()
    this.modal.close()
  }

  @action.bound
  setWorkspace(workspace: Workspace | null | undefined) {
    this.selectedWorkspace = workspace ?? null
  }

  @action.bound
  prefill({
    email,
    firstName,
    lastName,
    phone,
    workspaceId,
    workspace,
  }: {
    email?: string
    firstName?: string
    lastName?: string
    phone?: string
    workspaceId?: Id
    workspace?: Workspace | null
  }) {
    this.email = email?.trim() ?? ''
    this.firstName = firstName?.trim() ?? ''
    this.lastName = lastName?.trim() ?? ''
    this.phone = phone?.trim() ?? ''

    if (workspaceId) {
      const availableWorkspace =
        this.availableWorkspaces.find((item) => item.id === workspaceId) ??
        (workspace?.id === workspaceId ? workspace : null)

      if (availableWorkspace) {
        this.setWorkspace(availableWorkspace)
      } else {
        this.selectedWorkspace = null
      }
    }
  }

  @action.bound
  reset() {
    this.email = ''
    this.firstName = ''
    this.lastName = ''
    this.phone = ''
    this.selectedWorkspace = null
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const workspace = this.selectedWorkspace

    if (!workspace) {
      return
    }

    const createdMember = await this._createClient(workspace).catch(
      this._createAlertErrorHandler('Creating the client failed'),
    )

    this._newAlert()
      .withState('success')
      .saying('Client created')
      .autoDismiss()
      .show()

    this.close()

    if (createdMember) {
      this._onClientCreated({ workspace, member: createdMember })
    }
  })

  /**
   * Workspaces with external customers sign their clients in with a magic
   * link, so those get a customer instead of a local member.
   */
  private async _createClient(workspace: Workspace): Promise<Member | null> {
    const email = this.email.trim()
    const firstName = this.firstName.trim()
    const lastName = this.lastName.trim()
    const phone = this.phone.trim()

    if (workspace.config.externalCustomer.enabled) {
      const customer = await this._createCustomer({
        workspaceId: workspace.id,
        email,
        givenName: firstName,
        familyName: lastName,
        phoneNumber: phone || undefined,
      })

      return await this._fetchMember({
        workspaceId: workspace.id,
        userPublicId: customer.userPublicId,
      })
    }

    return await this._createMemberManually({
      workspaceId: workspace.id,
      email,
      givenName: firstName || undefined,
      familyName: lastName || undefined,
      phone: phone || undefined,
      source: 'Local',
      portal: 'CustomerPortal',
    })
  }

  /**
   * Sets up automatic clearing of validation errors when the field changes.
   */
  private _clearValidationOnChange(field: keyof this) {
    this._lifecycle.manage(
      toStream(() => this[field]).pipe(
        tap(() => this.validation.clearErrors(field as string)),
      ),
    )
  }
}
